// 개선 9건 차수2e(26-08-04) — 계약의 **배정·설치 단계**(Contract.handoffStatus) 표기와
// '아직 로봇이 안 붙었다' 판정의 정본.
//
// 왜 파일이 생겼나 — 고객사 상세가 계약별 카드를 그리면서 이 값을 처음 읽는다. 그런데
// 같은 라벨 세트가 이미 화면 안에 세 벌 흩어져 있었다(고객사 마스터 시트 · 필드 대시보드 ·
// 설치 캘린더). 네 벌째를 그 파일 안에 또 적으면 "같은 값을 화면마다 다른 단어로 부른다"가
// 한 걸음 더 굳는다. 그래서 표기 정본을 lib 에 두는 이 리포의 관례(lib/contract-reason,
// lib/amount-or-none, lib/d-day)를 따른다.
//
// 개선 9건 차수2f(26-08-04) — **수렴 완료.** 이 표를 읽는 화면은 이제 넷이고 표는 하나다:
//   • pages/customers/CustomerDetailPage.tsx   (계약 카드)
//   • pages/business/CustomerMasterPage.tsx    (다계약 안내)
//   • pages/dashboard/FieldDashboardPage.tsx   (설치 예정 목록)
//   • pages/field/InstallCalendarPage.tsx      (범례 + 날짜 칸 안내)
// 종전엔 뒤 세 화면이 같은 표를 각자 들고 있었고, 이 파일 머리 주석은 "같이 고쳐 달라"고
// **부탁**만 했다. 이 팀에서 부탁은 그물이 아니다 — 사본이 다시 생기면 깨지는 시험을
// 붙였다(lib/contract-handoff.test.ts '배정 단계 표기 정본 — 사본 금지').
//
// 설치 캘린더가 쓰던 다른 어휘(사인 전 단계를 다른 한 단어로 불렀다)는 **의도가 아니었다.**
// 확인한 것: ⓐ 그 화면에 근거 주석이 없다. ⓑ 계획서
// (docs/plan/team-dashboards-and-production-handoff/plan.md enum 정의)의 설명도 '사인 전'이라
// 그 단어와 무관하다. ⓒ 그 단어는 리포 안에서 그 한 줄 말고는 화면 문구로 쓰인 적이 없다.
// 그래서 별개 맵으로 남기지 않고 이 표 하나로 모았다 — 다만 그 화면의 범례·안내 문구가
// **눈에 보이게 바뀐 변경**이다(조용히 바꾸지 않는다. 필드팀이 그 단어를 원했다면 여기에
// 이름 있는 예외 맵을 두고 그 화면만 그것을 읽게 하면 된다 — 시험이 그 형태를 허용한다).

/**
 * 배정·설치 단계 한국어 표기. 서버 enum(ContractHandoffStatus) 5값.
 * 고객사 마스터 시트·필드 대시보드가 쓰던 문구와 같은 단어를 쓴다(두 화면이 같은 계약을
 * 두고 다른 말을 하지 않게).
 */
export const CONTRACT_HANDOFF_LABEL: Record<string, string> = {
  PENDING_SIGN: '사인 대기',
  SIGNED_AWAITING_ASSIGNMENT: '배정 대기',
  ASSIGNED_AWAITING_INSTALL: '설치 대기',
  INSTALLED: '설치 완료',
  CANCELLED: '취소',
};

/**
 * 단계 원문 → 사람이 읽는 문구. **모르는 값은 원문 그대로** 돌려준다 — 틀린 한국어로
 * 뭉개지 않는다(lib/contract-reason 이 이미 쓰는 규약).
 */
export function formatContractHandoff(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return CONTRACT_HANDOFF_LABEL[raw] ?? raw;
}

/**
 * 이 계약이 **아직 로봇 배정 전**인가 — '할당 요청'(제조팀 재촉) 대상 판정.
 *
 * 두 값만 참이다:
 *   • PENDING_SIGN              — 사인 전. 사인이 늦어도 제조 배정을 미리 재촉한다.
 *   • SIGNED_AWAITING_ASSIGNMENT — 사인 끝, 배정 대기. 재촉의 본래 대상.
 * ASSIGNED_AWAITING_INSTALL 은 **이미 붙은 뒤**라 제외한다(재촉할 것이 배정이 아니라
 * 설치다). INSTALLED·CANCELLED 도 당연히 제외.
 *
 * ⚠ 판정을 **계약 단위**로 하는 것이 핵심이다. 종전 고객사 상세는 진행 상태(고객사 한 칸,
 *   임박 계약 기준) + 배치 대수(고객사 전체) + 요구 대수(최신 계약)를 AND 로 섞어서,
 *   배정 대기인 판매 계약에 버튼이 **아예 뜨지 않았다**(운영 실사고: 슭돈 이대역본점).
 */
export function isAwaitingRobotAssignment(handoffStatus: string | null | undefined): boolean {
  return handoffStatus === 'PENDING_SIGN' || handoffStatus === 'SIGNED_AWAITING_ASSIGNMENT';
}
